import './App.css';
import React, { useState, useCallback, useEffect } from 'react';
import styles from "../src/styles/main.module.css";
import CitiesShowcaser from './CitiesShowcaser';
import Navbar from './components/Navbar';
import HeroCarousel from './components/HeroCarousel';
import CoastScene from './components/CoastScene';

export default function Home() {
  const [predictions, setPredictions] = useState({})
  const [currUploadedImg, setCurrUploadedImg] = useState()
  const [imgsArr, setImgsArr] = useState([])
  const [dragging, setDragging] = useState(false);

  const loading = imgsArr === true;
  const hasResults = Array.isArray(imgsArr) && imgsArr.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://citydetectbackend-production.up.railway.app", {
          method: "GET",
        });
        console.log("HIT SERVER, RESPONSE: ", response);
      } catch (err) {
        console.error("Error fetching:", err);
      }
    };

    fetchData();
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setCurrUploadedImg(URL.createObjectURL(file));
    }
    sendPhoto(file)
  }, []);

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      let downsampledFile = await downsampleImage(file)
      setCurrUploadedImg(URL.createObjectURL(downsampledFile))
    }

    sendPhoto(file)
  };

  const preventDefaults = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = () => setDragging(true);
  const handleDragLeave = () => setDragging(false);

  async function downsampleImage(file, maxWidth = 1600, maxHeight = 1600, quality = 1) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const aspect = width / height;
          if (width > height) {
            width = maxWidth;
            height = Math.round(maxWidth / aspect);
          } else {
            height = maxHeight;
            width = Math.round(maxHeight * aspect);
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: "image/jpeg" }));
            } else {
              reject(new Error("Canvas is empty"));
            }
          },
          "image/jpeg",
          quality
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  async function sendPhoto(file) {
    setPredictions(true)
    setImgsArr(true)

    let downsampleImg = await downsampleImage(file)

    const formData = new FormData();
    formData.append("file", downsampleImg);

    const response = await fetch("https://citydetectbackend-production.up.railway.app/predict", {
      method: "POST",
      body: formData,
    });

    const result = await response.json();
    setPredictions(result.results)
    setImgsArr(result.display_imgs_paths)
  }

  return (
    <div className={styles.main}>
      <Navbar />

      <header className={styles.hero}>
        <HeroCarousel className={styles.heroscene} />

        <div className={styles.herocopy}>
          <p className={styles.eyebrow}>Architectural Atlas</p>
          <h1 className={styles.headline}>Find the places that fit you.</h1>
          <p className={styles.lede}>
            Upload a photograph of a building, street, or landscape. A small neural network reads
            its architecture and points you toward your next favorite destination.
          </p>
        </div>

        <div
          className={`${styles.station} ${dragging ? styles.stationdrag : ""}`}
          onDrop={onDrop}
          onDragOver={preventDefaults}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <label className={styles.stationlabel} htmlFor="fileUpload">
            <svg className={styles.stationglyph} viewBox="0 0 48 40" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 30 L18 14 L28 24 L34 18 L44 28" />
              <path d="M4 36 H44" />
              <circle cx="35" cy="9" r="4" />
            </svg>
            <p className={styles.stationline}>Drop a building here</p>
            <p className={styles.stationsub}>or <b className={styles.stationpick}>choose a photo</b></p>
          </label>
          <input className={styles.hiddeninput} type="file" accept="image/*" onChange={onFileChange} id="fileUpload" />

          {currUploadedImg && (
            <figure className={styles.plate}>
              <img className={styles.plateimg} src={currUploadedImg} alt="Your uploaded building" />
              <figcaption className={styles.platecap}>Your building</figcaption>
            </figure>
          )}
        </div>
      </header>

      {loading && (
        <section className={styles.results}>
          <div className={styles.placeholder}>
            <span className={styles.placeholderdot} />
            Reading the streets, one roofline at a time
          </div>
        </section>
      )}

      {hasResults && (
        <section className={styles.results}>
          <p className={styles.resultseyebrow}>Kindred cities</p>
          <CitiesShowcaser citiesArr={predictions} imgsArr={imgsArr} />
        </section>
      )}

      <section className={styles.field}>
        <svg className={styles.fieldcloud} viewBox="0 0 210 112" aria-hidden="true">
          <path
            d="M34 86 C12 86 6 60 28 54 C22 28 58 20 74 38 C84 12 128 12 136 40 C166 26 202 44 190 70 C206 74 204 90 186 88 C182 98 166 98 160 89 C152 99 134 99 128 89 C120 99 102 99 96 89 C88 99 68 99 62 89 C56 96 40 95 34 86 Z"
            fill="var(--cloud)" stroke="var(--ink)" strokeWidth="2.5" strokeLinejoin="round"
          />
        </svg>
        <div className={styles.fieldinner}>
          <p className={styles.fieldeyebrow}>Under the hood</p>
          <h2 className={styles.fieldtitle}>A convolutional net, trained on thirteen thousand streets.</h2>
          <p className={styles.fieldbody}>
            It learned to see rooflines, windows, materials, and proportion — the
            quiet grammar of a place — then to measure which cities are speaking
            the same language.
          </p>
          <a className={styles.fieldlink} href="/model">Read how it was built</a>
        </div>

        <CoastScene embed className={styles.fieldscene} />
      </section>

      <footer className={styles.footer}>
        <span className={styles.footerrule} />
        <p className={styles.footertext}>
          Created by <a className={styles.footerlink} href="https://www.aaronanidjar.com/" target="_blank" rel="noopener noreferrer">Aaron Anidjar</a>
        </p>
      </footer>
    </div>
  );
}
